import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail, downloadFile, authFileUrl } from "@/lib/apiClient";
import { PageHeader, StatusBadge, EmptyState, money } from "@/components/shared";
import LineItemsEditor from "@/components/LineItemsEditor";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShoppingCart, Plus, Trash2, FileDown, FileCode, Paperclip, FileText, X } from "lucide-react";

const empty = { buyer_id: "", order_type: "raw", buyer_po_no: "", order_date: "", expected_date: "", status: "draft", notes: "", items: [], attachments: [] };

export default function PurchaseOrders() {
  const { user } = useAuth();
  const canManage = ["admin", "manager"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [products, setProducts] = useState([]);
  const [costings, setCostings] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/purchase-orders").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/buyers").then((r) => setBuyers(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/costings").then((r) => setCostings(r.data || []));
  }, []);

  // SKUs from both Products and Cost Sheets (so costed-but-not-yet-productised SKUs show up)
  const skuOptions = useMemo(() => {
    const byName = {};
    buyers.forEach((b) => { byName[(b.name || "").toLowerCase()] = b.id; });
    const map = new Map();
    products.forEach((p) => { if (p.sku) map.set(p.sku, { sku: p.sku, label: `${p.sku} · ${p.name}`, buyer_id: p.buyer_id || "" }); });
    costings.forEach((c) => {
      if (c.sku && !map.has(c.sku)) {
        map.set(c.sku, { sku: c.sku, label: `${c.sku} · ${c.product_name}`, buyer_id: c.buyer_id || byName[(c.buyer_name || "").toLowerCase()] || "" });
      }
    });
    let list = [...map.values()];
    if (form.buyer_id) list = list.filter((o) => !o.buyer_id || o.buyer_id === form.buyer_id);
    return list.map(({ sku, label }) => ({ sku, label }));
  }, [products, costings, buyers, form.buyer_id]);

  const create = async () => {
    try {
      const b = buyers.find((x) => x.id === form.buyer_id);
      await api.post("/purchase-orders", { ...form, buyer_name: b?.name || "", items: form.items.map((i) => ({ ...i, qty: Number(i.qty), rate: Number(i.rate) })) });
      toast.success("Purchase order created");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/purchase-orders/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const uploadAttachments = async (files) => {
    for (const file of files) {
      try {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/upload", fd);
        const ext = (file.name.split(".").pop() || "").toLowerCase();
        setForm((prev) => ({ ...prev, attachments: [...(prev.attachments || []), { name: file.name, path: data.path, ext }] }));
      } catch { toast.error(`Could not upload ${file.name}`); }
    }
    toast.success("Attachment(s) uploaded");
  };
  const removeAttachment = (i) => setForm((prev) => ({ ...prev, attachments: (prev.attachments || []).filter((_, x) => x !== i) }));

  return (
    <div className="space-y-8">
      <PageHeader title="Purchase Orders" subtitle="Raise and track material purchase orders for incoming stock." icon={ShoppingCart}
        action={canManage && (
          <div className="flex items-center gap-2">
          {canManage && (
            <Button variant="outline" data-testid="tally-export-po" onClick={() => downloadFile("/tally/purchases.xml", "aranya_purchases_tally.xml")} className="gap-2">
              <FileCode className="h-4 w-4" /> Export to Tally
            </Button>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-po-button" className="gap-2 transition-transform duration-200 hover:-translate-y-px"><Plus className="h-4 w-4" /> New PO</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle className="font-heading">New Purchase Order</DialogTitle></DialogHeader>
              <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2"><Label>Buyer</Label>
                    <Select value={form.buyer_id} onValueChange={(v) => setForm({ ...form, buyer_id: v })}>
                      <SelectTrigger data-testid="po-buyer"><SelectValue placeholder="Select buyer" /></SelectTrigger>
                      <SelectContent>{buyers.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="space-y-2"><Label>Order type</Label>
                    <Select value={form.order_type} onValueChange={(v) => setForm({ ...form, order_type: v })}>
                      <SelectTrigger data-testid="po-order-type"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="raw">Raw Product</SelectItem><SelectItem value="finished">Finished Product</SelectItem></SelectContent>
                    </Select></div>
                  <div className="space-y-2"><Label>Buyer PO No</Label>
                    <Input data-testid="po-buyer-po" value={form.buyer_po_no} onChange={(e) => setForm({ ...form, buyer_po_no: e.target.value })} placeholder="CB-2026-5567" /></div>
                  <div className="space-y-2"><Label>Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger data-testid="po-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="ordered">Ordered</SelectItem>
                        <SelectItem value="received">Received</SelectItem>
                      </SelectContent>
                    </Select></div>
                  <div className="space-y-2"><Label>Order date</Label>
                    <Input type="date" data-testid="po-date" value={form.order_date} onChange={(e) => setForm({ ...form, order_date: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Expected date</Label>
                    <Input type="date" value={form.expected_date} onChange={(e) => setForm({ ...form, expected_date: e.target.value })} /></div>
                </div>
                <LineItemsEditor label="Items" testid="po-items" items={form.items} setItems={(v) => setForm({ ...form, items: v })}
                  skuOptions={skuOptions} />
                <div className="space-y-2"><Label>Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Delivery / payment terms" /></div>
                <div className="space-y-2 rounded-md border border-border p-3">
                  <div className="flex items-center justify-between">
                    <Label>Attachments (original buyer PO)</Label>
                    <label className="cursor-pointer rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted">
                      <Paperclip className="mr-1 inline h-3.5 w-3.5" /> Add files
                      <input type="file" multiple className="hidden" data-testid="po-attachments"
                        onChange={(e) => e.target.files.length && uploadAttachments([...e.target.files])} />
                    </label>
                  </div>
                  {(form.attachments || []).length === 0
                    ? <p className="text-xs text-muted-foreground">Attach the buyer's original PO (PDF/image/doc). Multiple files allowed.</p>
                    : <div className="space-y-1">{form.attachments.map((a, i) => (
                        <div key={i} className="flex items-center justify-between rounded border border-border px-2 py-1 text-xs" data-testid={`po-attachment-${i}`}>
                          <span className="flex items-center gap-1 truncate"><FileText className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{a.name}</span></span>
                          <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeAttachment(i)}><X className="h-3.5 w-3.5" /></Button>
                        </div>))}
                      </div>}
                </div>
              </div>
              <DialogFooter><Button data-testid="po-save" onClick={create} disabled={!form.buyer_id}>Create PO</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )} />

      {items.length === 0 ? (
        <EmptyState icon={ShoppingCart} title="No purchase orders yet" hint="Create your first PO to record incoming material." />
      ) : (
        <Card className="overflow-hidden rounded-md border-border shadow-none">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>PO #</TableHead><TableHead>Buyer</TableHead><TableHead>Items</TableHead>
                <TableHead className="text-right">Value</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id} data-testid={`po-row-${p.id}`} className="transition-colors duration-200 hover:bg-muted/40">
                  <TableCell className="font-semibold">{p.po_number}</TableCell>
                  <TableCell>{p.buyer_name || p.supplier}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {p.items.length} items
                    {(() => {
                      const ord = p.items.reduce((s, li) => s + (Number(li.qty) || 0), 0);
                      const rec = p.items.reduce((s, li) => s + (Number(li.received) || 0), 0);
                      if (rec > 0) return <span className="ml-2 inline-flex rounded-full border border-sage/40 bg-sage/10 px-2 py-0.5 text-[11px] font-semibold text-sage" data-testid={`po-received-${p.id}`}>recv {rec}/{ord}</span>;
                      return null;
                    })()}
                    {(p.attachments || []).length > 0 && (
                      <span className="ml-2 inline-flex flex-wrap items-center gap-1" data-testid={`po-attachments-${p.id}`}>
                        {p.attachments.map((a, i) => (
                          <a key={i} href={authFileUrl(a.path)} target="_blank" rel="noreferrer" title={a.name}
                            className="inline-flex items-center gap-0.5 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px] text-primary hover:bg-muted">
                            <Paperclip className="h-3 w-3" /> PO{p.attachments.length > 1 ? ` ${i + 1}` : ""}</a>
                        ))}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-heading font-bold">{money(p.total)}</TableCell>
                  <TableCell><StatusBadge status={p.status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="icon" data-testid={`po-pdf-${p.id}`} onClick={() => downloadFile(`/purchase-orders/${p.id}/pdf`, `${p.po_number}.pdf`)}
                      className="h-8 w-8 text-muted-foreground hover:text-primary"><FileDown className="h-4 w-4" /></Button>
                    {canManage && (
                      <Button variant="ghost" size="icon" data-testid={`po-delete-${p.id}`} onClick={() => remove(p.id)}
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                    )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
